// Where the circle is centred and what the kreska calls it (SPEC-redesign §3,
// §5.3, §5.8). One resolver, because two would drift: S1 centres the map on it
// and S2 measures every distance from it, and „od Ciebie" appearing in a row
// while the map still sits on Stary Rynek is exactly the kind of lie this theme
// exists to remove.
import { here } from '../shared/geo.ts';
import { viewState, type ViewState } from './viewstate.ts';

export type ResolvedCentre = { lat: number; lon: number; name: string };

/** Where the circle sits when nobody has moved it. */
export const STARY_RYNEK: ResolvedCentre = { lat: 52.4083, lon: 16.9335, name: 'Stary Rynek' };

/**
 * Pure. `{ kind: 'me' }` resolves to the resident's own position only once the
 * browser has given us one: until then the centre stays Stary Rynek, because
 * a distance „od Ciebie" is never promised before `◎` (§5.8).
 */
export function resolveCentre(view: ViewState): ResolvedCentre {
  const centre = view.centre;
  if (centre.kind === 'picked') return { lat: centre.lat, lon: centre.lon, name: centre.name };
  if (centre.kind === 'me' && here.at) return { lat: here.at.lat, lon: here.at.lon, name: 'ja' };
  return STARY_RYNEK;
}

const same = (a: ResolvedCentre, b: ResolvedCentre) =>
  a.lat === b.lat && a.lon === b.lon && a.name === b.name;

/**
 * Fires when the centre actually moves, whichever of the two moved it: the view
 * (`tu`, or the crosshair putting it back) or the browser answering with a
 * position. Only when it moves — a radius drag changes the view without
 * changing the centre, and the map must not ease for that.
 */
export function onCentreChange(fn: (centre: ResolvedCentre) => void): () => void {
  let last = resolveCentre(viewState.get());
  const emit = () => {
    const next = resolveCentre(viewState.get());
    if (same(next, last)) return;
    last = next;
    fn(next);
  };
  const offView = viewState.subscribe(emit);
  const offHere = here.subscribe(emit);
  return () => {
    offView();
    offHere();
  };
}
